import type { ProductCategory, MilitarySubcategory } from "@/data/products";
import type { SupportedLocale } from "@/lib/i18n";
import { DEFAULT_LOCALE, t } from "@/lib/i18n";
import { CATEGORIES, MILITARY_SUBCATEGORIES } from "@/data/products";

function getContactPathForLocale(locale: SupportedLocale): string {
  return locale === DEFAULT_LOCALE ? `/${CONTACT_SLUG}` : `/${locale}/${CONTACT_SLUG}`;
}

const CONTACT_SLUG = "contact";

const STATIC_PAGE_SLUGS: Record<SupportedLocale, { about: string; why: string; where: string }> = {
  en: { about: "about-12go", why: "why-12go", where: "where-to-buy-12go" },
  de: { about: "uber-12go", why: "warum-12go", where: "wo-kaufen-12go" },
  es: { about: "acerca-de-12go", why: "por-que-12go", where: "donde-comprar-12go" },
  fr: { about: "a-propos-12go", why: "pourquoi-12go", where: "ou-acheter-12go" },
  nl: { about: "over-12go", why: "waarom-12go", where: "waar-kopen-12go" },
  pl: { about: "o-12go", why: "dlaczego-12go", where: "gdzie-kupic-12go" },
  zh: { about: "guanyu-12go", why: "weishenme-12go", where: "zai-nali-mai-12go" },
  // it/ru use English static slugs (pages/[locale]/about-12go etc.)
  it: { about: "about-12go", why: "why-12go", where: "where-to-buy-12go" },
  ru: { about: "about-12go", why: "why-12go", where: "where-to-buy-12go" },
};

/** Dla angielskiego (default) URL bez prefiksu /en. */
function localePrefix(locale: SupportedLocale): string {
  return locale === DEFAULT_LOCALE ? "" : `/${locale}`;
}

/** Slug „products” w URL – zależny od locale */
export function getProductsSlug(locale: SupportedLocale): string {
  return t(locale, "url.productsSlug" as const);
}

/** URL strony głównej dla locale (en = /, pozostałe = /pl itd.) */
export function getHomeUrl(locale: SupportedLocale): string {
  return locale === DEFAULT_LOCALE ? "/" : `/${locale}`;
}

/** URL strony „O nas” */
export function getAbout12GoUrl(locale: SupportedLocale): string {
  return `${localePrefix(locale)}/${STATIC_PAGE_SLUGS[locale].about}`.replace(/^\/\//, "/") || "/";
}

/** URL strony „Dlaczego 12GO” */
export function getWhy12GoUrl(locale: SupportedLocale): string {
  return `${localePrefix(locale)}/${STATIC_PAGE_SLUGS[locale].why}`.replace(/^\/\//, "/") || "/";
}

/** URL strony „Gdzie kupić” (z prefiksem locale, en bez prefiksu) */
export function getWhereToBuyUrl(locale: SupportedLocale): string {
  return `${localePrefix(locale)}/${STATIC_PAGE_SLUGS[locale].where}`.replace(/^\/\//, "/") || "/";
}

/** URL strony kontaktowej (en = /contact, pozostałe = /pl/contact itd.) */
export function getContactUrl(locale: SupportedLocale): string {
  return `${localePrefix(locale)}/${CONTACT_SLUG}`.replace(/^\/\//, "/") || "/";
}

/** Tworzy slug z tekstu (lowercase, spacje->myślniki, tylko litery/cyfry) */
function slugify(text: string): string {
  const map: Record<string, string> = {
    ą: "a", ę: "e", ó: "o", ł: "l", ć: "c", ń: "n", ś: "s", ź: "z", ż: "z",
    ß: "ss", ä: "a", ö: "o", ü: "u",
  };
  return text
    .toLowerCase()
    .split("")
    .map((c) => map[c] ?? c)
    .join("")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Slug produktu w formacie: slug-nazwy-produktu-n{numbering} */
export function getProductSlug(product: { name?: string; numbering: string }): string {
  const name = product.name ?? `12GO ${product.numbering}`;
  const base = slugify(name);
  return base ? `${base}-n${product.numbering}` : `n${product.numbering}`;
}

/**
 * URL strony produktu. Zawsze z prefiksem locale (włącznie z /en/).
 *
 * Strony produktów nie mają konkretnego pliku w `pages/` — istnieją wyłącznie
 * pod `/[locale]/[productsSlug]`. Client-side Pages Router nie zna middleware
 * rewrite, więc 1-segmentowy URL `/{slug}` powoduje `handleRouteInfoError`
 * → invariant „attempted to hard navigate to the same URL”. Prefiks `/en/`
 * daje 2 segmenty, które klient potrafi dopasować.
 *
 * Pozostałe strony EN (homepage, /sets, /about-12go, …) mają fizyczne pliki,
 * więc dla nich prefiks pozostaje pomijany (patrz getHomeUrl, getAllProductsUrl).
 */
export function getProductUrl(
  product: { name?: string; numbering: string },
  locale: SupportedLocale
): string {
  const slug = getProductSlug(product);
  return `/${locale}/${slug}`;
}

/** Sprawdza czy slug wygląda jak slug produktu (kończy się na -n{number}) */
export function isProductSlugPattern(slug: string): boolean {
  return /-n\d+$/.test(slug || "");
}

/** Wyciąga product id (numbering) ze sluga, np. e100wt-weapon-transport-vehicle-n97017 → 97017 */
export function parseProductIdFromSlug(slug: string): string {
  const match = slug?.match(/-n(\d+)$/);
  return match ? match[1] : slug || "";
}

/** Baza URL listy produktów (en bez prefiksu), np. /sets, /pl/zestawy */
export function getProductsBaseUrl(locale: SupportedLocale): string {
  const slug = getProductsSlug(locale);
  return locale === DEFAULT_LOCALE ? `/${slug}` : `/${locale}/${slug}`;
}

/**
 * Konwersja między slugami URL a wartościami enum – zależna od locale
 */

export function categoryToSlug(category: ProductCategory, locale: SupportedLocale): string {
  return t(locale, `categorySlug.${category}` as "categorySlug.military" | "categorySlug.civilian");
}

export function slugToCategory(slug: string, locale: SupportedLocale): ProductCategory | null {
  const normalized = slug?.trim().toLowerCase();
  if (!normalized) return null;
  for (const c of CATEGORIES) {
    if (categoryToSlug(c, locale).toLowerCase() === normalized) return c;
  }
  return null;
}

export function militarySubcategoryToSlug(
  subcategory: MilitarySubcategory,
  locale: SupportedLocale
): string {
  return t(locale, `militarySubSlug.${subcategory}` as "militarySubSlug.tanks_vehicles" | "militarySubSlug.ships" | "militarySubSlug.other");
}

export function slugToMilitarySubcategory(
  slug: string,
  locale: SupportedLocale
): MilitarySubcategory | null {
  const normalized = slug?.trim().toLowerCase();
  if (!normalized) return null;
  for (const sc of MILITARY_SUBCATEGORIES) {
    if (militarySubcategoryToSlug(sc, locale).toLowerCase() === normalized) return sc;
  }
  return null;
}

/**
 * Buduje URL dla kategorii (ze zlokalizowanymi slugami)
 */
export function getCategoryUrl(category: ProductCategory, locale: SupportedLocale): string {
  return `${getProductsBaseUrl(locale)}/${categoryToSlug(category, locale)}`;
}

/**
 * Buduje URL dla podkategorii wojskowej (ze zlokalizowanymi slugami)
 */
export function getMilitarySubcategoryUrl(
  subcategory: MilitarySubcategory,
  locale: SupportedLocale
): string {
  return `${getProductsBaseUrl(locale)}/${categoryToSlug("military", locale)}/${militarySubcategoryToSlug(subcategory, locale)}`;
}

/**
 * Buduje URL dla wszystkich produktów
 */
export function getAllProductsUrl(locale: SupportedLocale): string {
  return getProductsBaseUrl(locale);
}

/** Sprawdza czy slug pasuje do products w danym locale */
export function isProductsSlugValid(slug: string, locale: SupportedLocale): boolean {
  return getProductsSlug(locale).toLowerCase() === (slug || "").toLowerCase();
}

/** Zwraca ścieżkę produktów w nowym locale (dla zmiany języka) */
export function getProductsPathForLocale(
  pathSegments: string[],
  fromLocale: SupportedLocale,
  toLocale: SupportedLocale
): string | null {
  if (pathSegments.length === 0) return null;
  if (pathSegments.length === 1) {
    const currentSlug = pathSegments[0].toLowerCase();
    const fromStatic = STATIC_PAGE_SLUGS[fromLocale];
    if (currentSlug === fromStatic.about) return `/${STATIC_PAGE_SLUGS[toLocale].about}`;
    if (currentSlug === fromStatic.why) return `/${STATIC_PAGE_SLUGS[toLocale].why}`;
    if (currentSlug === fromStatic.where) return `/${STATIC_PAGE_SLUGS[toLocale].where}`;
    if (currentSlug === CONTACT_SLUG) return getContactPathForLocale(toLocale);
  }

  const productsSlug = getProductsSlug(fromLocale).toLowerCase();
  if (pathSegments[0].toLowerCase() !== productsSlug) return null;

  if (pathSegments.length === 1) {
    return getProductsBaseUrl(toLocale);
  }

  const category = slugToCategory(pathSegments[1], fromLocale);
  if (!category) return null;

  if (pathSegments.length === 2) {
    return getCategoryUrl(category, toLocale);
  }

  const subcategory = slugToMilitarySubcategory(pathSegments[2], fromLocale);
  if (!subcategory || category !== "military") return null;

  return getMilitarySubcategoryUrl(subcategory, toLocale);
}
